<!-- Top Bar — brand navy in light mode, neutral surface in dark mode.
     One responsive boundary on the bar's own width (DESIGN-SYSTEM.md §12.1): the nav
     menu, subscriber chip, and presentation toggle survive at every width; below 600px (NARROW)
     the brand hides, the chip goes icon-only, and theme + profile fold into the overflow ⋮. -->
<!-- gap-4 guarantees a minimum space between the brand cluster and the right cluster (chip /
     impersonation pill) even when the bar is crowded and the chip is shrinking. -->
<div #topBar class="top-bar fixed top-0 left-0 z-[997] flex h-[var(--top-bar-height)] w-full items-center gap-4 bg-(--p-primary-color) px-2 py-0 dark:border-b dark:border-(--p-content-border-color) dark:bg-(--p-content-background)">
    <!-- Logo Container -->
    <div class="flex shrink-0 basis-auto items-center gap-2 h-full">
        <!-- Menu Button -->
        <app-top-bar-button icon="menu" (clicked)="onMenuToggleClicked($event)"></app-top-bar-button>

        @if (isFull()) {
            <div class="h-6 border-l border-white/20 dark:border-(--p-content-border-color)"></div>

            <!-- Wordmark: "Angus Energy" text, hidden at NARROW with its divider (logo image isn't
                 editable; white reads on the navy bar, off-white on the dark bar) -->
            <span class="inline-flex select-none items-baseline gap-1 text-[1.25rem] tracking-tight text-white pl-2 dark:text-(--p-surface-100)">
                <span class="font-semibold">Angus</span> <span class="font-normal">Energy</span>
            </span>
        }
    </div>

    <!-- Right Top Bar Container -->
    <div class="flex min-w-0 shrink grow basis-auto items-center justify-end gap-2">
        @if (impersonationMessage(); as message) {
            <span
                class="inline-flex max-w-96 items-center gap-1.5 truncate rounded-full bg-amber-100/20 px-2.5 py-1 text-sm font-medium text-amber-50 ring-1 ring-amber-100/30 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20"
                [title]="message"
            >
                <span class="material-symbols-outlined text-[1rem]">switch_account</span>
                @if (isFull()) {
                    <span class="truncate">{{ message }}</span>
                } @else {
                    <!-- NARROW: the word alone marks the state; the full message stays in the tooltip. -->
                    <span>Impersonating</span>
                }
            </span>
        }

        <!-- SysAdmin context tools: global subscriber chip + presentation mode — first-class at every tier -->
        @if (presentationModeAvailable()) {
            <div class="flex min-w-0 items-center gap-2">
                <app-subscriber-chip-select [chipSize]="chipSize()" placeHolder="Select subscriber" [subscriberKey]="activeSubscriberKey()" (subscriberKeyChange)="onSubscriberKeyChanged($event)"></app-subscriber-chip-select>
                @if (isPresentationMode() && isFull()) {
                    <!-- FULL only: at NARROW the pressed cancel button + locked chip carry the signal. -->
                    <span class="rounded-full bg-amber-100/20 px-2.5 py-0.5 text-sm font-medium text-amber-50 ring-1 ring-amber-100/30 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20">Presenting</span>
                }
                <app-top-bar-button [icon]="isPresentationMode() ? 'cancel_presentation' : 'present_to_all'" [active]="isPresentationMode()" (clicked)="onPresentationModeToggleClicked()" [tooltip]="`${isPresentationMode() ? 'Exit' : 'Enter'} Presentation Mode`"></app-top-bar-button>
                @if (isFull()) {
                    <div class="h-6 border-l border-white/20 dark:border-(--p-content-border-color)"></div>
                }
            </div>
        }

        @if (isFull()) {
            <!-- Dark/Light Mode -->
            <app-top-bar-button [icon]="isDarkMode() ? 'light_mode' : 'dark_mode'" (clicked)="onDarkModeToggleClicked()" [tooltip]="`Switch to ${isDarkMode() ? 'Light' : 'Dark'} Mode`"></app-top-bar-button>

            <!-- Profile Menu -->
            <ng-template #profileMenu>
                <div class="top-bar-menu" cdkMenu>
                    @if (impersonationAvailable()) {
                        <div class="top-bar-menu-item-content" cdkMenuItem (click)="onImpersonateUserClicked()">
                            <span class="material-symbols-outlined">switch_account</span> Impersonate User
                        </div>
                        <hr />
                    }
                    <div class="top-bar-menu-item-content" cdkMenuItem (click)="onSignOutClicked()"><span class="material-symbols-outlined">logout</span> Sign out</div>
                </div>
            </ng-template>

            <app-top-bar-button icon="account_circle" [menu]="profileMenu" tooltip="Profile"></app-top-bar-button>
        } @else {
            <!-- NARROW: theme + profile actions fold into the overflow as flat rows
                 (locked decision — no nested menu; Sign out stays one tap away at every width). -->
            <ng-template #overflowMenu>
                <div class="top-bar-menu" cdkMenu>
                    <div class="top-bar-menu-item-content" cdkMenuItem (click)="onDarkModeToggleClicked()">
                        <span class="material-symbols-outlined">{{ isDarkMode() ? 'light_mode' : 'dark_mode' }}</span> {{ isDarkMode() ? 'Light Mode' : 'Dark Mode' }}
                    </div>
                    @if (impersonationAvailable()) {
                        <hr />
                        <div class="top-bar-menu-item-content" cdkMenuItem (click)="onImpersonateUserClicked()">
                            <span class="material-symbols-outlined">switch_account</span> Impersonate User
                        </div>
                    }
                    <hr />
                    <div class="top-bar-menu-item-content" cdkMenuItem (click)="onSignOutClicked()"><span class="material-symbols-outlined">logout</span> Sign out</div>
                </div>
            </ng-template>

            <app-top-bar-button icon="more_vert" [menu]="overflowMenu" tooltip="More options"></app-top-bar-button>
        }
    </div>
</div>

@if (impersonationAvailable()) {
    <app-impersonation-dialog [(visible)]="isImpersonationDialogVisible" />
}
